import React from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { CemeterySettingsProvider } from './contexts/CemeterySettingsContext';
import ErrorBoundary from './components/ErrorBoundary';
import Layout from './components/Layout';
import { ProtectedRoute } from './components/ProtectedRoute';
import { PermissionRoute } from './components/PermissionRoute';
import Login from './pages/Login';
import PrivacyPolicy from './pages/PrivacyPolicy';
import AccountDeletion from './pages/AccountDeletion';
import Dashboard from './pages/Dashboard';
import Contracts from './pages/Contracts';
import Cancellations from './pages/Cancellations';
import Relocations from './pages/Relocations';
import UnseenContracts from './pages/UnseenContracts';
import ContractForm from './pages/ContractForm';
import ContractFormV5 from './pages/ContractFormV5';
import ContractDetail from './pages/ContractDetail';
import ContractHistory from './pages/ContractHistory';
import ContractCosts from './pages/ContractCosts';
import ContractManagementFees from './pages/ContractManagementFees';
import ContractMaintenance from './pages/ContractMaintenance';
import MemoDetail from './pages/MemoDetail';
import GraveRecord from './pages/GraveRecord';
import Costs from './pages/Costs';
import ManagementFees from './pages/ManagementFees';
import Documents from './pages/Documents';
import Users from './pages/Users';
import Cemeteries from './pages/Cemeteries';
import Cemetery from './pages/Cemetery';
import Deceased from './pages/Deceased';
import StoneObjects from './pages/StoneObjects';
import Consultations from './pages/Consultations';
import Maintenance from './pages/Maintenance';
import Engraving from './pages/Engraving';
import CostStatistics from './pages/CostStatistics';
import CemeterySettings from './pages/CemeterySettings';
import Settings from './pages/Settings';
import Statistics from './pages/Statistics';
import AdminSettings from './pages/AdminSettings';
import VisitorHistory from './pages/VisitorHistory';
import AppDownload from './pages/AppDownload';
import PdfPreview from './pages/PdfPreview';
import './App.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5분
      cacheTime: 10 * 60 * 1000, // 10분
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ToastProvider>
            <CemeterySettingsProvider>
              <Router>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/account-deletion" element={<AccountDeletion />} />
                <Route path="/pdf-preview" element={
                  <ProtectedRoute>
                    <PdfPreview />
                  </ProtectedRoute>
                } />
                <Route path="/" element={
                  <ProtectedRoute>
                    <Layout>
                      <Outlet />
                    </Layout>
                  </ProtectedRoute>
                }>
                  <Route index element={
                    <PermissionRoute permission="CONTRACT_VIEW">
                      <Contracts />
                    </PermissionRoute>
                  } />
                  <Route path="contracts" element={
                    <PermissionRoute permission="CONTRACT_VIEW">
                      <Contracts />
                    </PermissionRoute>
                  } />
                  <Route path="cancellations" element={
                    <PermissionRoute permission="CONTRACT_VIEW">
                      <Cancellations />
                    </PermissionRoute>
                  } />
                  <Route path="relocations" element={
                    <PermissionRoute permission="CONTRACT_VIEW">
                      <Relocations />
                    </PermissionRoute>
                  } />
                  <Route path="unseen-contracts" element={
                    <PermissionRoute permission="CONTRACT_VIEW">
                      <UnseenContracts />
                    </PermissionRoute>
                  } />
                  <Route path="contracts/new" element={
                    <PermissionRoute permission="CONTRACT_CREATE">
                      <ContractFormV5 />
                    </PermissionRoute>
                  } />
                  <Route path="contracts/additional-burial/:id" element={
                    <PermissionRoute permission="CONTRACT_CREATE">
                      <ContractFormV5 isAdditionalBurial={true} />
                    </PermissionRoute>
                  } />
                  <Route path="contracts/edit/:id" element={
                    <PermissionRoute permission="CONTRACT_EDIT">
                      <ContractFormV5 />
                    </PermissionRoute>
                  } />
                  <Route path="contracts/:id" element={
                    <PermissionRoute permission="CONTRACT_VIEW">
                      <ContractDetail />
                    </PermissionRoute>
                  } />
                  <Route path="contracts/:id/histories" element={
                    <PermissionRoute permission="CONTRACT_VIEW">
                      <ContractHistory />
                    </PermissionRoute>
                  } />
                  <Route path="contracts/:id/costs" element={
                    <PermissionRoute permission="COST_VIEW">
                      <ContractCosts />
                    </PermissionRoute>
                  } />
                  <Route path="contracts/:id/management-fees" element={
                    <PermissionRoute permission="MANAGEMENT_FEE_VIEW">
                      <ContractManagementFees />
                    </PermissionRoute>
                  } />
                  <Route path="contracts/:id/maintenance" element={
                    <PermissionRoute permission="MAINTENANCE_VIEW">
                      <ContractMaintenance />
                    </PermissionRoute>
                  } />
                  <Route path="contracts/:id/memos" element={
                    <PermissionRoute permission="MEMO_VIEW">
                      <MemoDetail />
                    </PermissionRoute>
                  } />
                  <Route path="contracts/:id/grave-record" element={
                    <PermissionRoute permission="CONTRACT_VIEW">
                      <GraveRecord />
                    </PermissionRoute>
                  } />
                  <Route path="costs" element={
                    <PermissionRoute permission="COST_VIEW">
                      <Costs />
                    </PermissionRoute>
                  } />
                  <Route path="management-fees" element={
                    <PermissionRoute permission="MANAGEMENT_FEE_VIEW">
                      <ManagementFees />
                    </PermissionRoute>
                  } />
                  <Route path="documents" element={
                    <PermissionRoute permission="CONTRACT_VIEW">
                      <Documents />
                    </PermissionRoute>
                  } />
                  <Route path="users" element={
                    <PermissionRoute permission="USER_MANAGE">
                      <Users />
                    </PermissionRoute>
                  } />
                  <Route path="cemeteries" element={
                    <PermissionRoute permission="CEMETERY_VIEW">
                      <Cemeteries />
                    </PermissionRoute>
                  } />
                  <Route path="cemetery" element={
                    <PermissionRoute permission="CEMETERY_VIEW">
                      <Cemetery />
                    </PermissionRoute>
                  } />
                  <Route path="deceased" element={
                    <PermissionRoute permission="CONTRACT_VIEW">
                      <Deceased />
                    </PermissionRoute>
                  } />
                  <Route path="stone-objects" element={
                    <PermissionRoute permission="STONE_VIEW">
                      <StoneObjects />
                    </PermissionRoute>
                  } />
                  <Route path="consultations" element={
                    <PermissionRoute permission="CONTRACT_VIEW">
                      <Consultations />
                    </PermissionRoute>
                  } />
                  <Route path="maintenance" element={
                    <PermissionRoute permission="MAINTENANCE_VIEW">
                      <Maintenance />
                    </PermissionRoute>
                  } />
                  <Route path="engraving" element={
                    <PermissionRoute permission="CONTRACT_VIEW">
                      <Engraving />
                    </PermissionRoute>
                  } />
                  <Route path="cost-statistics" element={
                    <PermissionRoute permission="STATISTICS_VIEW">
                      <CostStatistics />
                    </PermissionRoute>
                  } />
                  <Route path="cemetery-settings" element={
                    <PermissionRoute permission="CEMETERY_VIEW">
                      <CemeterySettings />
                    </PermissionRoute>
                  } />
                  <Route path="settings" element={
                    <PermissionRoute permission="SYSTEM_SETTINGS">
                      <Settings />
                    </PermissionRoute>
                  } />
                  <Route path="statistics" element={
                    <PermissionRoute permission="STATISTICS_VIEW">
                      <Statistics />
                    </PermissionRoute>
                  } />
                  <Route path="admin-settings" element={
                    <PermissionRoute permission="ADMIN_PAGE_ACCESS">
                      <AdminSettings />
                    </PermissionRoute>
                  } />
                  <Route path="visitor-history" element={
                    <PermissionRoute permission="CONTRACT_VIEW">
                      <VisitorHistory />
                    </PermissionRoute>
                  } />
                  <Route path="app-download" element={
                    <PermissionRoute permission="CONTRACT_VIEW">
                      <AppDownload />
                    </PermissionRoute>
                  } />
                </Route>
              </Routes>
            </Router>
            </CemeterySettingsProvider>
          </ToastProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;